import { useState } from "react";
import { PackageCheck, Pencil } from "lucide-react";
import { LienFichier } from "@/composants/accuses-reception";
import { Bouton, LienBouton } from "@/composants/ui/bouton";
import { Carte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection, ChampTexte } from "@/composants/ui/champ";
import { Vide } from "@/composants/ui/etats";
import {
  MOTIFS_NON_CONFORMITE,
  dateSemaine,
  motifNonConformite,
  quantiteLisible,
} from "@/lib/libelles";
import {
  api,
  type CorrectionReception,
  type FicheCommandeFournisseur,
  type LigneReception,
  type MotifNonConformite,
  type ReceptionLogistique,
} from "@/lib/api";

type Agir = (geste: () => Promise<FicheCommandeFournisseur>) => Promise<boolean>;

const OPTIONS_MOTIF = [
  { valeur: "", libelle: "Choisir un motif" },
  ...Object.entries(MOTIFS_NON_CONFORMITE).map(([valeur, libelle]) => ({ valeur, libelle })),
];

const nombre = (texte: string) => Number(texte.replace(",", "."));

/**
 * Les réceptions logistiques d'une commande fournisseur (LOT 2, ticket #22) :
 * ce qui est arrivé, et parmi ce qui est arrivé ce qui n'est pas conforme, avec
 * son motif, son commentaire et ses photos. La base en tire le cumul reçu,
 * l'avancement de la commande et les problèmes qu'elle ouvre d'office — la
 * non-conformité, le surplus —, que la carte des problèmes montre.
 *
 * Un BL commun couvre aussi d'autres commandes du même fournisseur : son
 * document original s'ouvre entier à chaque chargé d'affaires concerné
 * (décision 45, ticket #48), et l'écran le dit commun.
 *
 * Une réception erronée se corrige (ticket #23), par qui constate et voit toutes
 * ses lignes : la correction se lit au journal, et le constat reste — une
 * non-conformité ne s'efface pas, et tranchée, elle ne se corrige plus.
 */
export function ReceptionsDeLaCommande({
  fiche,
  agir,
}: {
  fiche: FicheCommandeFournisseur;
  agir: Agir;
}) {
  const [enCorrection, setEnCorrection] = useState<string | null>(null);
  return (
    <Carte>
      <EnTeteCarte className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-encre">Réceptions logistiques</h2>
        {fiche.receptionnable ? (
          <LienBouton
            taille="compact"
            variante="secondaire"
            to={`/receptions/nouvelle?commandes=${fiche.id}`}
          >
            <PackageCheck className="h-4 w-4" aria-hidden />
            Constater une réception
          </LienBouton>
        ) : null}
      </EnTeteCarte>
      {fiche.receptions.length === 0 ? (
        <Vide titre="Aucune réception" detail="Rien n'est encore arrivé à l'atelier." />
      ) : (
        <ul className="divide-y divide-filet">
          {fiche.receptions.map((reception) => (
            <li key={reception.id} className="flex flex-col gap-3 px-4 py-3">
              <ReceptionDetail reception={reception} />
              {enCorrection === reception.id ? (
                <FormulaireCorrection
                  reception={reception}
                  onAnnuler={() => setEnCorrection(null)}
                  onValider={async (correction) => {
                    const faite = await agir(async () => {
                      await api.corrigerReception(reception.id, correction);
                      return api.commandeFournisseur(fiche.id);
                    });
                    if (faite) setEnCorrection(null);
                  }}
                />
              ) : reception.corrigeable ? (
                <span>
                  <Bouton
                    taille="compact"
                    variante="discret"
                    aria-label={`Corriger la réception du ${dateSemaine(reception.date_reception)}`}
                    onClick={() => setEnCorrection(reception.id)}
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    Corriger
                  </Bouton>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Carte>
  );
}

function ReceptionDetail({ reception }: { reception: ReceptionLogistique }) {
  return (
    <article
      aria-label={`Réception du ${dateSemaine(reception.date_reception)}`}
      className="flex flex-col gap-2 text-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-encre">
            Reçue le {dateSemaine(reception.date_reception)}, par {reception.receptionne_par}
          </p>
          <p className="text-encre-douce">
            {reception.numero_bl ? `BL ${reception.numero_bl}` : "Sans numéro de BL"}
            {reception.bl_commun ? " — BL commun à plusieurs commandes du fournisseur" : ""}
          </p>
          {reception.commentaire ? <p className="text-encre">{reception.commentaire}</p> : null}
        </div>
        {reception.bl ? (
          reception.bl.lisible ? (
            reception.bl_commun ? (
              <LienFichier
                documentId={reception.bl.id}
                libelle="Voir le document original du BL commun"
                texte="Voir le document original"
              />
            ) : (
              <LienFichier documentId={reception.bl.id} libelle="Fichier du BL" />
            )
          ) : (
            <p className="text-xs text-encre-douce">Le fichier du BL ne vous est pas ouvert.</p>
          )
        ) : null}
      </div>
      <ul className="flex flex-col gap-1.5">
        {reception.lignes.map((ligne) => (
          <li key={ligne.ligne_commande_fournisseur_id}>
            <LigneRecue ligne={ligne} />
          </li>
        ))}
      </ul>
      {reception.photos.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-encre-douce">Photos :</span>
          {reception.photos.map((photo) =>
            photo.lisible ? (
              <LienFichier
                key={photo.id}
                documentId={photo.id}
                libelle={`Photo ${photo.nom_fichier_origine}`}
              />
            ) : (
              <span key={photo.id} className="text-xs text-encre-douce">
                {photo.nom_fichier_origine}
              </span>
            ),
          )}
        </div>
      ) : null}
    </article>
  );
}

function LigneRecue({ ligne }: { ligne: LigneReception }) {
  const reperes = [ligne.numero_commande_client, ligne.code_lot].filter(Boolean).join(" · ");
  return (
    <div className="flex flex-col gap-0.5 rounded-[6px] border border-filet px-3 py-2">
      <p className="break-words text-encre">
        <span className="font-medium">{ligne.designation}</span>{" "}
        <span className="text-encre-douce">({reperes})</span>
      </p>
      <p className="text-encre">
        {ligne.quantite_annoncee !== null
          ? `Annoncé par le BL ${quantiteLisible(ligne.quantite_annoncee)} ${ligne.unite}, `
          : ""}
        {ligne.quantite_recue === 0
          ? "rien reçu"
          : `${ligne.quantite_annoncee !== null ? "reçu" : "Reçu"} ` +
            `${quantiteLisible(ligne.quantite_recue)} ${ligne.unite}`}
        {ligne.quantite_recue === 0
          ? ""
          : ligne.quantite_non_conforme > 0
            ? `, dont ${quantiteLisible(ligne.quantite_non_conforme)} ${ligne.unite} non conforme` +
              ` (${motifNonConformite(ligne.motif_non_conformite)})`
            : ", conforme"}
      </p>
      {ligne.commentaire ? <p className="text-encre-douce">{ligne.commentaire}</p> : null}
      {ligne.photos.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-encre-douce">Photos de cette ligne :</span>
          {ligne.photos.map((photo) =>
            photo.lisible ? (
              <LienFichier
                key={photo.id}
                documentId={photo.id}
                libelle={`Montre cette ligne : ${photo.nom_fichier_origine}`}
                texte={photo.nom_fichier_origine}
              />
            ) : (
              <span key={photo.id} className="text-xs text-encre-douce">
                {photo.nom_fichier_origine}
              </span>
            ),
          )}
        </div>
      ) : null}
    </div>
  );
}

type SaisieLigne = { recue: string; nonConforme: string; motif: string; commentaire: string };

function FormulaireCorrection({
  reception,
  onAnnuler,
  onValider,
}: {
  reception: ReceptionLogistique;
  onAnnuler: () => void;
  onValider: (correction: CorrectionReception) => Promise<void>;
}) {
  const [lignes, setLignes] = useState<Record<string, SaisieLigne>>(() =>
    Object.fromEntries(
      reception.lignes.map((ligne) => [
        ligne.ligne_commande_fournisseur_id,
        {
          recue: String(ligne.quantite_recue),
          nonConforme: String(ligne.quantite_non_conforme),
          motif: ligne.motif_non_conformite ?? "",
          commentaire: ligne.commentaire ?? "",
        },
      ]),
    ),
  );
  const [commentaire, setCommentaire] = useState(reception.commentaire ?? "");
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const ecrire = (id: string, champs: Partial<SaisieLigne>) =>
    setLignes((avant) => ({ ...avant, [id]: { ...avant[id], ...champs } }));
  return (
    <form
      aria-label="Corriger la réception"
      className="flex flex-col gap-3 rounded-[6px] border border-filet p-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onValider({
          motif: motif.trim(),
          commentaire: commentaire.trim() || null,
          lignes: reception.lignes.map((ligne) => {
            const saisie = lignes[ligne.ligne_commande_fournisseur_id];
            const nonConforme = nombre(saisie.nonConforme) || 0;
            return {
              ligne_commande_fournisseur_id: ligne.ligne_commande_fournisseur_id,
              quantite_recue: nombre(saisie.recue),
              quantite_non_conforme: nonConforme,
              motif_non_conformite:
                nonConforme > 0 ? ((saisie.motif || null) as MotifNonConformite | null) : null,
              commentaire: saisie.commentaire.trim() || null,
            };
          }),
        });
        setEnvoi(false);
      }}
    >
      {reception.lignes.map((ligne) => (
        <LigneACorriger
          key={ligne.ligne_commande_fournisseur_id}
          ligne={ligne}
          saisie={lignes[ligne.ligne_commande_fournisseur_id]}
          ecrire={(champs) => ecrire(ligne.ligne_commande_fournisseur_id, champs)}
        />
      ))}
      <ChampTexte
        libelle="Commentaire de la réception"
        value={commentaire}
        onChange={(evenement) => setCommentaire(evenement.target.value)}
      />
      <Champ
        libelle="Motif de la correction"
        required
        value={motif}
        onChange={(evenement) => setMotif(evenement.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !motif.trim()}>
          Enregistrer la correction
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

function LigneACorriger({
  ligne,
  saisie,
  ecrire,
}: {
  ligne: LigneReception;
  saisie: SaisieLigne;
  ecrire: (champs: Partial<SaisieLigne>) => void;
}) {
  const nonConforme = nombre(saisie.nonConforme) > 0;
  return (
    <fieldset
      aria-label={ligne.designation}
      className="flex min-w-0 flex-col gap-3 rounded-[6px] border border-filet p-3"
    >
      <legend className="sr-only">{ligne.designation}</legend>
      <p className="break-words text-sm font-medium text-encre">{ligne.designation}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Champ
          libelle="Reçu"
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          required
          value={saisie.recue}
          onChange={(evenement) => ecrire({ recue: evenement.target.value })}
        />
        <Champ
          libelle="Dont non conforme"
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          disabled={ligne.non_conformite_tranchee}
          aide={
            ligne.non_conformite_tranchee
              ? "La non-conformité est tranchée : elle ne se corrige plus."
              : undefined
          }
          value={saisie.nonConforme}
          onChange={(evenement) => ecrire({ nonConforme: evenement.target.value })}
        />
        {nonConforme ? (
          <ChampSelection
            libelle="Motif"
            required
            disabled={ligne.non_conformite_tranchee}
            value={saisie.motif}
            options={OPTIONS_MOTIF}
            onChange={(evenement) => ecrire({ motif: evenement.target.value })}
          />
        ) : null}
      </div>
      <Champ
        libelle="Commentaire"
        value={saisie.commentaire}
        onChange={(evenement) => ecrire({ commentaire: evenement.target.value })}
      />
    </fieldset>
  );
}
